import React, { useCallback, useEffect, useMemo, useState } from 'react';

import chevron_down from '../../../../../assets/images/chevron-down.svg';
import right_arrow from '../../../../../assets/images/right_arrow.svg';
import refresh from '../../../../../assets/images/arrow-counterclockwise.svg';
import lock_fill from '../../../../../assets/images/lock-fill.svg';
import no_medias_icon from '../../../../../assets/images/no_medias.svg';
import delete_icon from '../../../../../assets/images/delete.svg';
import star from '../../../../../assets/images/star.svg';
import star_select from '../../../../../assets/images/star_select.svg';
import portalThemeThumbnail from '../../../../../assets/images/portal-theme-thumbnail.png';

import { joinClassNames } from '../../../../../services/elementHelperService';

import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	FormGroup,
	IconButton,
	MenuItem,
	OutlinedInput,
	Select,
	Typography,
} from '@material-ui/core';
import {
	contentHeroVideoCustomizeSubKeys,
	contentPlaylistCustomizeSubKeys,
	defaultHeaderObject,
	defaultPageObject,
	defaultStylingObject,
	fontFamilyOptions,
	footerShowCallToActionSubKeys,
	footerShowSocialsSubKeys,
	level2Keys,
	pageSettingKeys,
	PLAYBACK_INITIATE_PLAYER_ON,
	PLAYBACK_INITIATE_PLAYER_ON_GRID_VIEW,
	playerKeys,
	playlistDisplayTypes,
	playlistTitleTypes,
	portalThemeSettingType,
	stylingKeys,
	stylingKeysNeedToBeHex,
	stylingObjectKeys,
	stylingObjects,
	translationKeys,
} from '../../../../../constants/portalThemesConstant';
import ColorPicker from '../../../../../reusable/ColorPicker/ColorPicker';
import { useTranslation } from 'react-i18next';
import useThemes, { Theme } from './hooks/useThemes';
import useExpendedSection, { SECTION_DEFAULT_THEMES } from './hooks/useExpandSection';
import LoadingOverlay from 'react-loading-overlay';
import { ScaleLoader } from 'react-spinners';
import { isEqual } from 'lodash';

import './PortalThemes.scss';
import ConfirmationDialog from '../../../../../reusable/ConfirmationDialog/ConfirmationDialog';
import usePlayerConfiguration from './hooks/usePlayerConfiguration';
import { portalsUrl } from '../../../../../utils/config';
import { Close } from '@material-ui/icons';
import { defaultSettingTypes } from '../../../../../constants/playerConfigurationsConstant';
import { a11yLanguages, a11yLanguagesTranslateLabels } from '../../../../../constants/languageConstants';

type PortalCreationDialogProps = {
	isOpen: boolean;
	editingTheme: Theme;
	saveAsNewTheme: (_themeName: string, _callback: () => void) => void;
	onClose: () => void;
};

(LoadingOverlay as any).propTypes = undefined;
const PortalCreationDialog = ({ isOpen, editingTheme, saveAsNewTheme, onClose }: PortalCreationDialogProps) => {
	const { t } = useTranslation();
	const [themeName, setThemeName] = useState<string>('');

	useEffect(() => {
		setThemeName(`${editingTheme.name}`);
	}, [editingTheme]);

	return (
		<Dialog open={isOpen} fullWidth maxWidth="sm" onClose={onClose}>
			<DialogTitle className="dialogTitle" disableTypography>
				<Typography variant="h6">{t('SETTINGS_PRESET_CONFIG_SAVE_AS_PRESET_TITLE')}</Typography>
				<IconButton onClick={onClose}>
					<Close />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				<div className="dialogContentWrapper">
					<div className="dialogContent__item">
						<label>{t('SETTINGS_PRESET_CONFIG_NAME_OF_PRESET')}</label>
						<input
							value={themeName}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								setThemeName(event.target.value);
							}}
						/>
					</div>
				</div>
			</DialogContent>
			<DialogActions>
				<div className="dialogActionsWrapper">
					<div className="dialogBoxInternalBlock dialogBtnBottomContainer">
						<Button
							type="button"
							variant="contained"
							className="defaultActionBtn"
							onClick={() => {
								saveAsNewTheme(themeName, onClose);
							}}
						>
							Save
						</Button>
					</div>
				</div>
			</DialogActions>
		</Dialog>
	);
};

const PortalTheme = () => {
	const { t, i18n } = useTranslation();

	const {
		loadingData,
		themesToRender,
		editingThemeId,
		userEditingThemeChanged,
		updatingEditingThemeId,
		updatingUserChange,
		saveChangeTheme,
		saveAsNewTheme,
		resetDefaultTheme,
		deleteATheme,
		toggleThemeFavorite,
	} = useThemes();

	const { playerConfigurationToRender } = usePlayerConfiguration();

	const [deleteThemeId, setDeleteThemeId] = useState<string>('');
	const [isShowCreationDialog, setIsShowCreationDialog] = useState<boolean>(false);
	const [expandedCards, setExpandedCards] = useState<string[]>([
		...Object.keys(portalThemeSettingType).map((k) => (portalThemeSettingType as any)[k]),
	]);
	const [previewPortalUrl, setPreviewPortalUrl] = useState<string>('');

	const { expandedSection, updateExpandSection } = useExpendedSection();
	const {
		defaultThemes: defaultThemesLoading,
		accountThemes: accountThemesLoading,
		defaultThemesId: defaultThemesIdLoading,
	} = loadingData;

	const showLoadingOverlay = defaultThemesLoading || accountThemesLoading || defaultThemesIdLoading;
	const isDefaultThemesSectionExpands = expandedSection.includes(SECTION_DEFAULT_THEMES);
	const defaultThemesSectionItems = themesToRender.filter((theme) => theme.isDefault);
	const accountThemes = themesToRender.filter((theme) => !theme.isDefault);
	const originalEditingTheme = themesToRender.find((theme) => theme.themeId === editingThemeId);

	// As BE dont support add new property ezlly, and we don't have time to update BE
	//=> Using styling property to store all the styling properties for header, content, footer
	const headerKeys = useMemo(
		() => stylingKeys.filter((key) => key.startsWith('header') || key === 'showFavicon'),
		[]
	);
	const contentKeys = useMemo(() => stylingKeys.filter((key) => key.startsWith('content')), []);
	const footerKeys = useMemo(() => stylingKeys.filter((key) => key.startsWith('footer')), []);

	const { name, isDefault, styling } = userEditingThemeChanged || {};
	const accountPlayerPreset = playerConfigurationToRender.filter((config) => !config.isDefault);
	const accountId = localStorage.getItem('defaultAccountId');

	const expandCard = useCallback((cardID: string, e: React.MouseEvent) => {
		const nodeName = (e.target as any).nodeName.toLowerCase();
		if (nodeName === 'span' || nodeName === 'input') {
			return;
		}

		setExpandedCards((prevState) => {
			if (prevState.includes(cardID)) {
				return prevState.filter((s) => s !== cardID);
			}
			return [...prevState, cardID];
		});
	}, []);

	useEffect(() => {
		if (!userEditingThemeChanged) {
			return;
		}

		const handler = setTimeout(() => {
			setPreviewPortalUrl(
				`${portalsUrl}preview?theme=${encodeURIComponent(JSON.stringify(styling))}&playerPresetId=${
					userEditingThemeChanged.playback?.defaultPlayerPreset ?? 'Enterprise'
				}&accountId=${accountId}&lang=${userEditingThemeChanged.styling?.defaultPageLanguage ||
																			defaultPageObject.defaultPageLanguage}`
			);
		}, 500);

		return () => {
			clearTimeout(handler);
		};
	}, [styling, userEditingThemeChanged, accountId, i18n.language]);

	if (showLoadingOverlay) {
		return (
			<LoadingOverlay
				className="loadingStreamOverlay"
				active={true}
				spinner={<ScaleLoader color={'#ffffff'} />}
				text={`${t("COMMON_LOADING")}...`}
			/>
		);
	}

	if (!userEditingThemeChanged) {
		return (
			<div className="portalThemesWrapper">
				<div className="portalThemesWrapper__no-data">
					<img src={no_medias_icon} />
					<label> {t('MEDIA_LIBRARY_MEDIA_DISPLAY_THERE_IS_NOTHING_HERE')}</label>
				</div>
			</div>
		);
	}

	return (
		<div className="portalThemesWrapper">
			<div className="configurationThemes">
				<div className="configurationThemes__header">{t('SETTINGS_PORTALS_PRESETS_HEADER')}</div>

				<div
					className="configurationThemes__sub-header"
					onClick={() => {
						updateExpandSection('defaultThemes');
					}}
				>
					<span>{t("SETTINGS_PORTAL_CHOOSE_LAYOUT")}</span>
					<img
						className={joinClassNames([
							'playerConfigurations-arrow',
							isDefaultThemesSectionExpands ? 'playerConfigurations-arrow--expanded' : '',
						])}
						src={isDefaultThemesSectionExpands ? chevron_down : right_arrow}
					/>
				</div>

				{isDefaultThemesSectionExpands && (
					<>
						<div className="configurationThemes__default-themes">
							{defaultThemesSectionItems.map(({ themeId, name }) => {
								const isSelected = editingThemeId === themeId;
								return (
									<div
										key={themeId}
										className={`theme-item ${isSelected ? 'theme-item--selected' : ''}`}
										onClick={() => {
											updatingEditingThemeId(themeId);
										}}
									>
										<img src={portalThemeThumbnail} alt={name} style={{ width: '100%' }} />
										<div className="theme-item__label--title">{name}</div>
									</div>
								);
							})}
						</div>
						<div className="configurationThemes__saved-themes">
							<label>{t("MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_DETAILS_PLAYER_INFO_SAVED_PRESETS")}</label>
							<FormControl
								fullWidth
								className={`saved-theme-select__control ${
									accountThemes.find((theme) => theme.themeId === editingThemeId) ? 'selected' : ''
								}`}
							>
								<Select
									id="saved-theme-selector"
									value={
										themesToRender.find(
											({ isDefault, themeId }) => !isDefault && themeId === editingThemeId
										)
											? editingThemeId
											: 0
									}
									onChange={(
										event: React.ChangeEvent<{
											name?: string;
											value: unknown;
										}>
									) => {
										updatingEditingThemeId(event.target.value as string);
									}}
									disableUnderline
								>
									<MenuItem disabled value={0} className="saved-themes-option">
										{t(accountThemes.length === 0 ? 'SETTINGS_PORTAL_THEME_OPTION_NO_CUSTOM_PRESETS' : 'SETTINGS_PORTAL_THEME_OPTION_PLACEHOLDER') as string}
									</MenuItem>
									{accountThemes.map(({ themeId, name, favorite }) => {
										return (
											<MenuItem key={themeId} value={themeId} className="saved-themes-option">
												<div className="saved-themes-option__content">
													{name}
													<div className="saved-themes-option__action">
														<button
															type="button"
															className="saved-themes-option--star"
															onClick={(event) => {
																event.stopPropagation();
																toggleThemeFavorite(themeId);
															}}
														>
															<img src={favorite ? star_select : star} />
														</button>
														<button
															type="button"
															className="saved-themes-option--delete"
															onClick={(event) => {
																event.stopPropagation();
																setDeleteThemeId(themeId);
															}}
														>
															<img src={delete_icon} />
														</button>
													</div>
												</div>
											</MenuItem>
										);
									})}
								</Select>
							</FormControl>
						</div>
					</>
				)}

				<div className="configurationTheme-editing-title sub-header">
					<span>
						{t('SETTINGS_PRESET_CONFIG_PLAYER_CONFIG_OPTION_HEADER')}
						<i>{name ?? 'Unknown'}</i>
					</span>
				</div>

				<div className="configurationTheme-editing-name borderBottom playerConfigurations-preset-name">
					<span>{t('SETTINGS_PRESET_CONFIG_NAME_OF_PRESET')}</span>
					<div className="playerConfigurations-preset-name__input configurationTheme__input">
						<input
							value={name}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const value = e.target.value;
								updatingUserChange('name', value);
							}}
							disabled={isDefault}
						/>
						{isDefault && (
							<div className="configurationThemes_tooltip-container">
								<label className="tooltiptext">
									{t('SETTINGS_PRESET_CONFIG_PLAYER_CONFIG_PRESET_NAME_LOCK')}
								</label>
								<img src={lock_fill} />
							</div>
						)}
					</div>
				</div>

				<div className="configurationThemes-notice">{t("SETTINGS_PORTAL_PRESET_CHANGE_DES")}</div>

				<div className="configurationTheme-cards">
					{Object.values(portalThemeSettingType)
						.sort(
							(keyA: string, keyB: string) =>
								(portalThemeSettingType as any)[keyA] - (portalThemeSettingType as any)[keyB]
						)
						.map((settingKey: any) => {
							return (
								<div key={settingKey} className="configurationTheme-card">
									<div
										className="configurationTheme-card__header"
										onClick={(e) => expandCard(settingKey, e)}
									>
										{t(translationKeys[settingKey as keyof typeof translationKeys])}
										<div className={'playerConfigurations-card__header-actions'}>
											{isDefault && (
												<button
													className="playerConfigurations-card__refresh-btn"
													onClick={() => {
														resetDefaultTheme(settingKey);
													}}
												>
													<img src={refresh} /> {t('SETTINGS_PRESET_CONFIG_RESET_TO_DEFAULT')}
												</button>
											)}
											<img
												className={joinClassNames([
													'playerConfigurations-arrow',
													expandedCards.includes(settingKey)
														? 'playerConfigurations-arrow--expanded'
														: '',
												])}
												src={expandedCards.includes(settingKey) ? chevron_down : right_arrow}
											/>
										</div>
									</div>
									{expandedCards.includes(settingKey) && (
										<div className="configurationTheme-card__content">
											{settingKey === portalThemeSettingType.Styling &&
												stylingObjects.map((objKey) => {
													return (
														<FormGroup key={objKey}>
															<div
																className="configurationTheme__control playerConfigurations-card__control configurationTheme__control playerConfigurations-card__control--title"
																style={{ fontWeight: 'bold', pointerEvents: 'none' }}
															>
																<label>
																	{t(translationKeys[objKey as keyof typeof translationKeys])}
																</label>
															</div>
															{((stylingObjectKeys as any)[objKey] ?? []).map(
																(key: any) => {
																	if (key === 'fontFamily') {
																		return (
																			<div
																				key={key}
																				className="configurationTheme__control playerConfigurations-card__control configurationTheme__control playerConfigurations-card__control--item"
																			>
																				<label
																					id={key + '-select-label'}
																					htmlFor={key + '-select'}
																				>
																					{t(
																						translationKeys[
																							key as keyof typeof translationKeys
																						]
																					)}
																				</label>
																				<Select
																					labelId={key + '-select-label'}
																					id={key + '-select'}
																					value={
																						userEditingThemeChanged.styling?.[key] ?? ''
																					}
																					displayEmpty
																					disableUnderline
																					onChange={(
																						event: React.ChangeEvent<{
																							name?: string;
																							value: unknown;
																						}>
																					) => {
																						updatingUserChange(
																							`${settingKey}.${key}`,
																							event.target.value as string
																						);
																					}}
																				>
																					{fontFamilyOptions.map(({ key: optKey, label }) => (
																						<MenuItem
																							key={optKey}
																							value={optKey}
																							className="player-config-select-option"
																						>
																							<>{t(label)}</>
																						</MenuItem>
																					))}
																				</Select>
																			</div>
																		);
																	}

																	if (stylingKeysNeedToBeHex.includes(key)) {
																		return (
																			<div
																				key={key}
																				className="configurationTheme__control playerConfigurations-card__control configurationTheme__control playerConfigurations-card__control--item"
																			>
																				<label
																					id={key + '-select-label'}
																					htmlFor={key + '-select'}
																				>
																					{t(
																						translationKeys[
																							key as keyof typeof translationKeys
																						]
																					)}
																				</label>
																				<div className="playerConfigurations-card__color-picker">
																					<span>{t("COMMON_COLOR")}</span>
																					<ColorPicker
																						width="20px"
																						height="20px"
																						borderRadius="4px"
																						padding="2px"
																						color={
																							userEditingThemeChanged
																								.styling?.[key] ??
																							(
																								defaultStylingObject as any
																							)[key]
																						}
																						onChangeComplete={(
																							color: any
																						) => {
																							updatingUserChange(
																								`${settingKey}.${key}`,
																								color.hex
																							);
																						}}
																					/>
																				</div>
																			</div>
																		);
																	}

																	return (
																		<div
																			key={key}
																			className="configurationTheme__control playerConfigurations-card__control  configurationTheme__control playerConfigurations-card__control--item"
																		>
																			<label
																				id={key + '-select-label'}
																				htmlFor={key + '-select'}
																			>
																				{t(
																					translationKeys[
																						key as keyof typeof translationKeys
																					]
																				)}
																			</label>
																			<FormControlLabel
																				labelPlacement="start"
																				control={
																					<OutlinedInput
																						className="configurationTheme__input"
																						value={
																							userEditingThemeChanged
																								.styling?.[key] ?? ''
																						}
																						onChange={(
																							event: React.ChangeEvent<
																								| HTMLTextAreaElement
																								| HTMLInputElement
																							>
																						) => {
																							const value =
																								event.target.value;
																							updatingUserChange(
																								`${settingKey}.${key}`,
																								value
																							);
																						}}
																					/>
																				}
																				label={null}
																			/>
																		</div>
																	);
																}
															)}
														</FormGroup>
													);
												})}

											{settingKey === portalThemeSettingType.Styling && (
												<FormGroup>
													<div
														className="configurationTheme__control playerConfigurations-card__control configurationTheme__control playerConfigurations-card__control--title"
														style={{ fontWeight: 'bold', pointerEvents: 'none' }}
													>
														<label>Custom styling</label>
													</div>
													<div className="configurationTheme__control playerConfigurations-card__control configurationTheme__custom-styling">
														<textarea
															className="configurationTheme__custom-styling-textarea"
															value={userEditingThemeChanged.styling?.customStyling ?? ''}
															onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
																updatingUserChange('styling.customStyling', event.target.value);
															}}
															placeholder="/* Enter custom CSS here */"
														/>
													</div>
												</FormGroup>
											)}

											{settingKey === portalThemeSettingType.Playback && (
												<FormGroup>
													{playerKeys.map((key) => {
														if (key === 'defaultPlayerPreset') {
															return (
																<div
																	key={key}
																	className="configurationTheme__control playerConfigurations-card__control"
																>
																	<label
																		id={key + '-select-label'}
																		htmlFor={key + '-select'}
																	>
																		{t(
																			translationKeys[
																				key as keyof typeof translationKeys
																			]
																		)}
																	</label>

																	<Select
																		labelId={'player-config-select-label'}
																		id={'player-config-select'}
																		value={
																			userEditingThemeChanged.playback?.[key] ??
																			''
																		}
																		disableUnderline
																		style={{ marginTop: '6px' }}
																		onChange={(
																			event: React.ChangeEvent<{
																				name?: string;
																				value: unknown;
																			}>
																		) => {
																			const value = event.target.value as string;
																			updatingUserChange(
																				`${settingKey}.${key}`,
																				value
																			);
																		}}
																	>
																		<MenuItem
																			key={0}
																			value={0}
																			className="player-config-select-option"
																			disabled
																		>
																			<>
																				{t(
																					'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_DETAILS_PLAYER_INFO_CHOOSE_PLAYER'
																				)}
																			</>
																		</MenuItem>
																		<div className="player-config-select__subheader">
																			{t(
																				'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_DETAILS_PLAYER_INFO_DEFAULT_PRESETS'
																			)}
																		</div>
																		{playerConfigurationToRender
																			.filter((config) => config.isDefault)
																			.map((config, index) => {
																				return (
																					<MenuItem
																						key={index}
																						value={config.id}
																						className="player-config-select-option"
																					>
																						<div className="player-config-select-option__default-label">
																							<div className="player-config-select-option__primary-text">
																								{
																									defaultSettingTypes[
																										config.id.toLowerCase() as keyof typeof defaultSettingTypes
																									]?.name
																								}
																							</div>
																							<div className="player-config-select-option__secondary-text">
																								{t(
																									defaultSettingTypes[
																										config.id.toLowerCase() as keyof typeof defaultSettingTypes
																									]?.note
																								)}
																							</div>
																						</div>
																					</MenuItem>
																				);
																			})}
																		{accountPlayerPreset.length > 0 && <Divider />}
																		{accountPlayerPreset.length > 0 && (
																			<div className="player-config-select__subheader">
																				{t(
																					'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_DETAILS_PLAYER_INFO_SAVED_PRESETS'
																				)}
																			</div>
																		)}
																		{playerConfigurationToRender.map(
																			(config, index) => {
																				if (config.isDefault) {
																					return null;
																				}
																				return (
																					<MenuItem
																						key={index}
																						value={config.id}
																						className="player-config-select-option"
																					>
																						{config.name}
																					</MenuItem>
																				);
																			}
																		)}
																	</Select>
																</div>
															);
														}

														if (key === 'initiatePlayerOn' || key === 'initiatePlayerOnMobile') {
															return (
																<div
																	key={key}
																	className="configurationTheme__control playerConfigurations-card__control"
																>
																	<label
																		id={key + '-select-label'}
																		htmlFor={key + '-select'}
																	>
																		{t(
																			translationKeys[
																				key as keyof typeof translationKeys
																			]
																		)}
																	</label>
																	<Select
																		value={
																			userEditingThemeChanged.playback?.[key] ||
																			(key === 'initiatePlayerOnMobile' ? "" : PLAYBACK_INITIATE_PLAYER_ON_GRID_VIEW)
																		}
																		disableUnderline
																		style={{ marginTop: '6px' }}
																		onChange={(
																			event: React.ChangeEvent<{
																				name?: string;
																				value: unknown;
																			}>
																		) => {
																			const value = event.target.value as string;
																			updatingUserChange(
																				`${settingKey}.${key}`,
																				value
																			);
																		}}
																		displayEmpty
																	>
																		<MenuItem
																			key={"empty-option"}
																			value={""}
																			className="player-config-select-option"
																		> - </MenuItem>
																		{PLAYBACK_INITIATE_PLAYER_ON.map((type) => {
																			return (
																				<MenuItem
																					key={type}
																					value={type}
																					className="player-config-select-option"
																				>
																					<>
																						{t(
																							translationKeys[
																								type as keyof typeof translationKeys
																							]
																						)}
																					</>
																				</MenuItem>
																			);
																		})}
																	</Select>
																</div>
															);
														}
													})}
												</FormGroup>
											)}

											{settingKey === portalThemeSettingType.Header && (
												<FormGroup>
													{headerKeys.map((key) => {
														if (key === 'headerShowLogoType') {
															return (
																<div
																	key={key}
																	className="configurationTheme__control playerConfigurations-card__control"
																>
																	<FormControl>
																		<FormControlLabel
																			control={
																				<Checkbox
																					style={{ color: '#126CFC' }}
																					checked={Boolean(
																						userEditingThemeChanged
																							.styling?.[key] ??
																							defaultHeaderObject[key]
																					)}
																				/>
																			}
																			label={
																				t(
																					translationKeys[
																						key as keyof typeof translationKeys
																					]
																				)
																			}
																			onChange={(_e, checked) => {
																				updatingUserChange(
																					`styling.${key}`,
																					checked
																				);
																			}}
																		/>
																	</FormControl>
																	<OutlinedInput
																		className="configurationTheme__input"
																		disabled={
																			!Boolean(
																				userEditingThemeChanged.styling?.[
																					key
																				] ?? defaultHeaderObject[key]
																			)
																		}
																		value={
																			userEditingThemeChanged.styling
																				?.headerLogoTypeUrl
																		}
																		onChange={(
																			event: React.ChangeEvent<
																				HTMLTextAreaElement | HTMLInputElement
																			>
																		) => {
																			const value = event.target.value;
																			updatingUserChange(
																				`styling.headerLogoTypeUrl`,
																				value
																			);
																		}}
																	/>
																</div>
															);
														}

														return (
															<div
																key={key}
																className="configurationTheme__control playerConfigurations-card__control"
																style={{ height: 36 }}
															>
																<FormControl>
																	<FormControlLabel
																		control={
																			<Checkbox
																				style={{ color: '#126CFC' }}
																				checked={Boolean(
																					userEditingThemeChanged.styling?.[
																						key
																					] ??
																						(defaultHeaderObject as any)[
																							key
																						]
																				)}
																			/>
																		}
																		label={
																			t(
																				translationKeys[
																					key as keyof typeof translationKeys
																				]
																			)
																		}
																		onChange={(_e, checked) => {
																			updatingUserChange(
																				`styling.${key}`,
																				checked
																			);
																		}}
																	/>
																</FormControl>
															</div>
														);
													})}
												</FormGroup>
											)}

											{settingKey === portalThemeSettingType.Content && (
												<FormGroup>
													{contentKeys.map((key) => {
														if (key === 'contentPlaylistCustomize') {
															return (
																<>
																	<div
																		key={key}
																		className="configurationTheme__control playerConfigurations-card__control"
																	>
																		<label
																			id={key + '-select-label'}
																			htmlFor={key + '-select'}
																		>
																			{
																				t(
																					translationKeys[
																						key as keyof typeof translationKeys
																					]
																				)
																			}
																		</label>
																	</div>
																	{contentPlaylistCustomizeSubKeys.map((subKey) => {
																		return (
																			<div
																				key={subKey}
																				className="configurationTheme__control playerConfigurations-card__control"
																				style={{ marginLeft: 10 }}
																			>
																				{subKey ===
																					'contentPlaylistDisplay' && (
																					<>
																						<label
																							id={
																								subKey + '-select-label'
																							}
																							htmlFor={subKey + '-select'}
																						>
																							{t(
																								translationKeys[
																									subKey as keyof typeof translationKeys
																								]
																							)}
																						</label>
																						<Select
																							labelId={
																								'playlist-type-select-label'
																							}
																							id={'playlist-type-select'}
																							value={
																								userEditingThemeChanged
																									.styling?.[
																									subKey
																								] ?? 'grid'
																							}
																							disableUnderline
																							style={{ marginTop: '6px' }}
																							onChange={(
																								event: React.ChangeEvent<{
																									name?: string;
																									value: unknown;
																								}>
																							) => {
																								const value = event
																									.target
																									.value as string;
																								updatingUserChange(
																									`styling.${subKey}`,
																									value
																								);

																								if (value === 'grid') {
																									updatingUserChange(
																										`styling.contentPlaylistTitleType`,
																										'normal'
																									);
																								}

																								if (
																									value === 'carousel'
																								) {
																									updatingUserChange(
																										`styling.contentPlaylistRows`,
																										1
																									);
																								}
																							}}
																						>
																							{playlistDisplayTypes.map(
																								({ key, label }) => (
																									<MenuItem
																										key={key}
																										value={key}
																										className="player-config-select-option"
																									>
																										<>{t(label)}</>
																									</MenuItem>
																								)
																							)}
																						</Select>
																					</>
																				)}
																				{subKey === 'contentPlaylistRows' && (
																					<>
																						<label
																							id={
																								subKey + '-select-label'
																							}
																							htmlFor={subKey + '-select'}
																						>
																							{t(
																								translationKeys[
																									subKey as keyof typeof translationKeys
																								]
																							)}
																						</label>
																						<Select
																							labelId={
																								'playlist-type-select-label'
																							}
																							id={'playlist-type-select'}
																							value={
																								userEditingThemeChanged
																									.styling?.[
																									subKey
																								] ?? 1
																							}
																							disabled={
																								userEditingThemeChanged
																									.styling?.[
																									'contentPlaylistDisplay'
																								] === 'carousel'
																							}
																							disableUnderline
																							style={{ marginTop: '6px' }}
																							onChange={(
																								event: React.ChangeEvent<{
																									name?: string;
																									value: unknown;
																								}>
																							) => {
																								const value = event
																									.target
																									.value as string;
																								updatingUserChange(
																									`styling.${subKey}`,
																									value
																								);
																							}}
																						>
																							{[1, 2, 3, 4, 5].map(
																								(key) => (
																									<MenuItem
																										key={key}
																										value={key}
																										className="player-config-select-option"
																									>
																										<>{key}</>
																									</MenuItem>
																								)
																							)}
																						</Select>
																					</>
																				)}

																				{subKey ===
																					'contentPlaylistShowTitle' && (
																					<>
																						<FormControl>
																							<FormControlLabel
																								control={
																									<Checkbox
																										style={{
																											color: '#126CFC',
																										}}
																										checked={Boolean(
																											userEditingThemeChanged
																												.styling?.[
																												subKey
																											]
																										)}
																									/>
																								}
																								label={t(
																									translationKeys[
																										subKey as keyof typeof translationKeys
																									]
																								)}
																								onChange={(
																									_e,
																									checked
																								) => {
																									updatingUserChange(
																										`styling.${subKey}`,
																										checked
																									);
																								}}
																							/>
																						</FormControl>
																						<Select
																							labelId={
																								'playlist-show-title-select-label'
																							}
																							id={
																								'playlist-show-title-select'
																							}
																							value={
																								userEditingThemeChanged
																									.styling?.[
																									'contentPlaylistTitleType'
																								] ?? 'normal'
																							}
																							disabled={
																								!userEditingThemeChanged
																									.styling?.[subKey]
																							}
																							disableUnderline
																							style={{ marginTop: '6px' }}
																							onChange={(
																								event: React.ChangeEvent<{
																									name?: string;
																									value: unknown;
																								}>
																							) => {
																								const value = event
																									.target
																									.value as string;
																								updatingUserChange(
																									`styling.contentPlaylistTitleType`,
																									value
																								);
																							}}
																						>
																							{playlistTitleTypes.map(
																								({ key, label }) => (
																									<MenuItem
																										key={key}
																										value={key}
																										disabled={
																											key ===
																												'highlighted' &&
																											(!userEditingThemeChanged
																												.styling?.[
																												'contentPlaylistDisplay'
																											] ||
																												userEditingThemeChanged
																													.styling?.[
																													'contentPlaylistDisplay'
																												] ===
																													'grid')
																										}
																										className="player-config-select-option"
																									>
																										<>{label}</>
																									</MenuItem>
																								)
																							)}
																						</Select>
																					</>
																				)}
																			</div>
																		);
																	})}
																</>
															);
														}

														if (key === 'contentHeroVideoCustomize') {
															return (
																<>
																	<div
																		key={key}
																		className="configurationTheme__control playerConfigurations-card__control"
																	>
																		<label
																			id={key + '-select-label'}
																			htmlFor={key + '-select'}
																		>
																			{t(
																				translationKeys[
																					key as keyof typeof translationKeys
																				]
																			)}
																		</label>
																	</div>

																	{contentHeroVideoCustomizeSubKeys.map((subKey) =>
																		subKey === 'contentHeroVideoShowTextBox' ? (
																			<div
																				key={subKey}
																				className="configurationTheme__control playerConfigurations-card__control"
																				style={{
																					marginLeft: level2Keys.includes(
																						subKey
																					)
																						? 24
																						: 10,
																					height: 36,
																				}}
																			>
																				<FormControl key={subKey}>
																					<FormControlLabel
																						control={
																							<Checkbox
																								style={{
																									color: '#126CFC',
																								}}
																								checked={Boolean(
																									userEditingThemeChanged
																										.styling?.[
																										subKey
																									]
																								)}
																							/>
																						}
																						label={t(
																							translationKeys[
																								subKey as keyof typeof translationKeys
																							]
																						)}
																						onChange={(_e, checked) => {
																							updatingUserChange(
																								`styling.${subKey}`,
																								checked
																							);
																						}}
																					/>
																				</FormControl>
																			</div>
																		) : (
																			<div
																				key={subKey}
																				className="configurationTheme__control playerConfigurations-card__control"
																				style={{
																					marginLeft: level2Keys.includes(
																						subKey
																					)
																						? 24
																						: 10,
																				}}
																			>
																				<label
																					id={subKey + '-select-label'}
																					htmlFor={subKey + '-select'}
																				>
																					{t(
																						translationKeys[
																							subKey as keyof typeof translationKeys
																						]
																					)}
																				</label>
																				<OutlinedInput
																					className="configurationTheme__input"
																					value={
																						userEditingThemeChanged
																							.styling?.[subKey] ?? ''
																					}
																					onChange={(
																						event: React.ChangeEvent<
																							| HTMLTextAreaElement
																							| HTMLInputElement
																						>
																					) => {
																						const value =
																							event.target.value;
																						updatingUserChange(
																							`styling.${subKey}`,
																							value
																						);
																					}}
																					disabled={
																						!Boolean(
																							userEditingThemeChanged
																								.styling
																								?.contentHeroVideoShowTextBox
																						)
																					}
																				/>
																			</div>
																		)
																	)}
																</>
															);
														}

														return (
															<div
																key={key}
																className="configurationTheme__control playerConfigurations-card__control"
															>
																<FormControl>
																	<FormControlLabel
																		control={
																			<Checkbox
																				style={{ color: '#126CFC' }}
																				checked={Boolean(
																					userEditingThemeChanged.styling?.[
																						key
																					]
																				)}
																			/>
																		}
																		label={
																			t(
																				translationKeys[
																					key as keyof typeof translationKeys
																				]
																			)
																		}
																		onChange={(_e, checked) => {
																			updatingUserChange(
																				`styling.${key}`,
																				checked
																			);
																		}}
																	/>
																</FormControl>
															</div>
														);
													})}
												</FormGroup>
											)}

											{settingKey === portalThemeSettingType.Footer && (
												<FormGroup>
													{footerKeys.map((key) => {
														if (key === 'footerShowCallToAction') {
															return (
																<>
																	<div
																		key={key}
																		className="configurationTheme__control playerConfigurations-card__control"
																		style={{ height: 36 }}
																	>
																		<FormControl>
																			<FormControlLabel
																				control={
																					<Checkbox
																						style={{ color: '#126CFC' }}
																						checked={Boolean(
																							userEditingThemeChanged
																								.styling?.[key]
																						)}
																					/>
																				}
																				label={t(
																					translationKeys[
																						key as keyof typeof translationKeys
																					]
																				)}
																				onChange={(_e, checked) => {
																					updatingUserChange(
																						`styling.${key}`,
																						checked
																					);
																				}}
																			/>
																		</FormControl>
																	</div>
																	{footerShowCallToActionSubKeys.map((subKey) => {
																		return (
																			<div
																				key={subKey}
																				className="configurationTheme__control playerConfigurations-card__control"
																				style={{ marginLeft: 10 }}
																			>
																				<label
																					id={subKey + '-select-label'}
																					htmlFor={subKey + '-select'}
																				>
																					{t(
																						translationKeys[
																							subKey as keyof typeof translationKeys
																						]
																					)}
																				</label>
																				<OutlinedInput
																					className="configurationTheme__input"
																					value={
																						userEditingThemeChanged
																							.styling?.[subKey] ?? ''
																					}
																					onChange={(
																						event: React.ChangeEvent<
																							| HTMLTextAreaElement
																							| HTMLInputElement
																						>
																					) => {
																						const value =
																							event.target.value;
																						updatingUserChange(
																							`styling.${subKey}`,
																							value
																						);
																					}}
																					disabled={
																						!Boolean(
																							userEditingThemeChanged
																								.styling?.[key]
																						)
																					}
																				/>
																			</div>
																		);
																	})}
																</>
															);
														}

														if (key === 'footerShowSocials') {
															return (
																<>
																	<div
																		key={key}
																		className="configurationTheme__control playerConfigurations-card__control"
																	>
																		<FormControl>
																			<FormControlLabel
																				control={
																					<Checkbox
																						style={{ color: '#126CFC' }}
																						checked={Boolean(
																							userEditingThemeChanged
																								.styling?.[key]
																						)}
																					/>
																				}
																				label={t(
																					translationKeys[
																						key as keyof typeof translationKeys
																					]
																				)}
																				onChange={(_e, checked) => {
																					updatingUserChange(
																						`styling.${key}`,
																						checked
																					);
																				}}
																			/>
																		</FormControl>
																	</div>
																	{footerShowSocialsSubKeys.map((subKey) => {
																		return (
																			<div
																				key={subKey}
																				className="configurationTheme__control playerConfigurations-card__control"
																				style={{ marginLeft: 10 }}
																			>
																				<label
																					id={subKey + '-select-label'}
																					htmlFor={subKey + '-select'}
																				>
																					{t(
																						translationKeys[
																							subKey as keyof typeof translationKeys
																						]
																					)}
																				</label>
																				<OutlinedInput
																					className="configurationTheme__input"
																					value={
																						userEditingThemeChanged
																							.styling?.[subKey] ?? ''
																					}
																					onChange={(
																						event: React.ChangeEvent<
																							| HTMLTextAreaElement
																							| HTMLInputElement
																						>
																					) => {
																						const value =
																							event.target.value;
																						updatingUserChange(
																							`styling.${subKey}`,
																							value
																						);
																					}}
																					disabled={
																						!Boolean(
																							userEditingThemeChanged
																								.styling?.[key]
																						)
																					}
																				/>
																			</div>
																		);
																	})}
																</>
															);
														}

														if (key === 'footerSimple') {
															return (
																<>
																	<div
																		key={key}
																		className="configurationTheme__control playerConfigurations-card__control"
																		style={{ height: 36 }}
																	>
																		<FormControl>
																			<FormControlLabel
																				control={
																					<Checkbox
																						style={{ color: '#126CFC' }}
																						checked={Boolean(
																							userEditingThemeChanged.styling?.[key]
																						)}
																					/>
																				}
																				label={t(
																					translationKeys[
																						key as keyof typeof translationKeys
																					]
																				)}
																				onChange={(_e, checked) => {
																					updatingUserChange(
																						`styling.${key}`,
																						checked
																					);
																				}}
																			/>
																		</FormControl>
																	</div>
																	<div
																		className="configurationTheme__control playerConfigurations-card__control"
																		style={{ marginLeft: 10 }}
																	>
																		<label
																			id="footerSimpleContent-select-label"
																			htmlFor="footerSimpleContent-select"
																		>
																			{t(translationKeys.footerSimpleContent)}
																		</label>
																		<OutlinedInput
																			className="configurationTheme__input"
																			value={
																				userEditingThemeChanged.styling?.footerSimpleContent ?? ''
																			}
																			onChange={(
																				event: React.ChangeEvent<
																					HTMLTextAreaElement | HTMLInputElement
																				>
																			) => {
																				updatingUserChange(
																					'styling.footerSimpleContent',
																					event.target.value
																				);
																			}}
																			disabled={
																				!Boolean(
																					userEditingThemeChanged.styling?.[key]
																				)
																			}
																		/>
																	</div>
																</>
															);
														}

														return (
															<div
																key={key}
																className="configurationTheme__control playerConfigurations-card__control"
															>
																<FormControl>
																	<FormControlLabel
																		control={
																			<Checkbox
																				style={{ color: '#126CFC' }}
																				checked={Boolean(
																					userEditingThemeChanged.styling?.[
																						key
																					]
																				)}
																			/>
																		}
																		label={t(
																			translationKeys[
																				key as keyof typeof translationKeys
																			]
																		)}
																		onChange={(_e, checked) => {
																			updatingUserChange(
																				`styling.${key}`,
																				checked
																			);
																		}}
																	/>
																</FormControl>
															</div>
														);
													})}
												</FormGroup>
											)}

											{settingKey === portalThemeSettingType.Page && (
												<FormGroup>
													{pageSettingKeys.map((key) => {
														if (key === 'showFavicon') {
															return (
																<div
																	key={key}
																	className="configurationTheme__control playerConfigurations-card__control"
																>
																	<FormControl>
																		<FormControlLabel
																			control={
																				<Checkbox
																					style={{ color: '#126CFC' }}
																					checked={Boolean(
																						userEditingThemeChanged
																							.styling?.[key] ??
																							defaultPageObject[key]
																					)}
																				/>
																			}
																			label={t(
																				translationKeys[
																					key as keyof typeof translationKeys
																				]
																			)}
																			onChange={(_e, checked) => {
																				updatingUserChange(
																					`styling.${key}`,
																					checked
																				);
																			}}
																		/>
																	</FormControl>
																	<OutlinedInput
																		className="configurationTheme__input"
																		disabled={
																			!Boolean(
																				userEditingThemeChanged.styling?.[
																					key
																				] ?? defaultPageObject[key]
																			)
																		}
																		value={
																			userEditingThemeChanged.styling?.faviconUrl
																		}
																		onChange={(
																			event: React.ChangeEvent<
																				HTMLTextAreaElement | HTMLInputElement
																			>
																		) => {
																			const value = event.target.value;
																			updatingUserChange(
																				`styling.faviconUrl`,
																				value
																			);
																		}}
																		placeholder={t(
																			translationKeys.showFaviconPlaceHolder
																		)}
																	/>
																</div>
															);
														}
														if (key === 'defaultPageLanguage') {
															return (
																<div
																	key={key}
																	className="configurationTheme__control playerConfigurations-card__control"
																>
																	<FormControl>
																		<FormControlLabel
																			control={
																				<Checkbox
																					style={{ color: '#126CFC' }}
																					checked={Boolean(
																						userEditingThemeChanged
																							.styling?.[key] ??
																							defaultPageObject[key]
																					)}
																				/>
																			}
																			label={t(
																				translationKeys[
																					key as keyof typeof translationKeys
																				]
																			)}
																			onChange={(_e, checked) => {
																				updatingUserChange(
																					`styling.${key}`,
																					checked
																				);
																			}}
																		/>
																	</FormControl>
																	<Select
																		value={
																			userEditingThemeChanged.styling?.[key] ||
																			defaultPageObject[key]
																		}
																		disableUnderline
																		onChange={(
																			event: React.ChangeEvent<{
																				name?: string;
																				value: unknown;
																			}>
																		) => {
																			const value = event.target.value as string;
																			updatingUserChange(`styling.${key}`, value);
																		}}
																	>
																		{a11yLanguages.map((lang, index) => (
																			<MenuItem
																				key={index}
																				value={lang.languageCode}
																				className="a11y-lang-select-option"
																			>
																				{t(a11yLanguagesTranslateLabels[lang.languageCode as keyof typeof a11yLanguagesTranslateLabels].name) as string}
																			</MenuItem>
																		))}
																	</Select>
																</div>
															);
														}

														return (
															<div
																key={key}
																className="configurationTheme__control playerConfigurations-card__control"
															>
																<FormControl>
																	<FormControlLabel
																		control={
																			<Checkbox
																				style={{ color: '#126CFC' }}
																				checked={Boolean(
																					userEditingThemeChanged.styling?.[
																						key
																					]
																				)}
																			/>
																		}
																		label={t(
																			translationKeys[
																				key as keyof typeof translationKeys
																			]
																		)}
																		onChange={(_e, checked) => {
																			updatingUserChange(
																				`styling.${key}`,
																				checked
																			);
																		}}
																	/>
																</FormControl>
															</div>
														);
													})}
												</FormGroup>
											)}
										</div>
									)}
								</div>
							);
						})}
				</div>
			</div>
			<div className="previewThemes">
				<iframe width={'100%'} height={'100%'} src={previewPortalUrl} />
			</div>

			{(!isEqual(originalEditingTheme, userEditingThemeChanged) ||
				originalEditingTheme?.themeId?.includes('defaultTheme')) && (
				<div className="portal-theme-actions">
					{!userEditingThemeChanged.isDefault && (
						<Button
							type="button"
							variant="contained"
							className="defaultActionBtn squireBtn"
							onClick={saveChangeTheme}
						>
							{t('SETTINGS_PRESET_CONFIG_PLAYER_CONFIG_SAVE_CHANGE_BTN')}
						</Button>
					)}

					<Button
						type="button"
						variant="contained"
						className="defaultActionBtn squireBtn"
						onClick={() => {
							setIsShowCreationDialog(true);
						}}
					>
						{t('SETTINGS_PRESET_CONFIG_PLAYER_CONFIG_SAVE_AS_PRESET_BTN')}
					</Button>
				</div>
			)}

			{deleteThemeId && (
				<ConfirmationDialog
					title={t('SETTINGS_PRESET_CONFIG_PRESET_DELETE_CONFIRMATION_TITLE')}
					message={t('SETTINGS_PRESET_CONFIG_PRESET_DELETE_CONFIRMATION_MESSAGE')}
					callback={({ ok }) => {
						if (ok) {
							deleteATheme(deleteThemeId);
						}
						setDeleteThemeId('');
					}}
					okButtonText={t('COMMON_DELETE')}
				/>
			)}

			<PortalCreationDialog
				isOpen={isShowCreationDialog}
				editingTheme={userEditingThemeChanged}
				saveAsNewTheme={saveAsNewTheme}
				onClose={() => {
					setIsShowCreationDialog(false);
				}}
			/>
		</div>
	);
};

export default PortalTheme;
